// Photos and album details come from the gallery API, which the admin page
// edits. Photo URLs carry a version so browsers and the CDN cache them forever.

export const TAGS = ['Automotive', 'Portrait', 'Street', 'Landscape', 'Astro'];

let galleryPromise;

export const getGallery = () => {
	if (!galleryPromise) {
		galleryPromise = fetch('/api/gallery', {
			headers: { Accept: 'application/json' },
		})
			.then((response) => {
				if (!response.ok)
					throw new Error(`Gallery request failed: ${response.status}`);

				return response.json();
			})
			.catch((err) => {
				// Let the next caller retry instead of caching the failure.
				galleryPromise = undefined;
				throw err;
			});
	}

	return galleryPromise;
};

// The admin page calls this with fresh data so the public pages in the same
// tab don't keep showing albums or photos that were just changed or deleted.
export const setGallery = (gallery) => {
	galleryPromise = Promise.resolve(gallery);
};

export const getAlbums = async () => {
	try {
		const gallery = await getGallery();

		return gallery.albums || {};
	} catch (err) {
		console.error(err);

		return {};
	}
};

export const photoSrc = (albumId, variant, key, photo) =>
	`/photos/${encodeURIComponent(albumId)}/${variant}/${encodeURIComponent(
		key
	)}${photo?.v ? `?v=${photo.v}` : ''}`;

// Light theme shows photos without the white border.
export const themeVariant = (theme) =>
	theme === 'light' ? 'nonbordered' : 'bordered';

// Display order. The public API sends `order` (photo ids are random, so
// they can't be sorted); admin data has file names to sort by instead.
export const sortedPhotoKeys = (album) => {
	const photos = album?.photos || {};

	if (Array.isArray(album?.order))
		return album.order.filter((key) => key in photos);

	const name = (key) => photos[key]?.name || key;

	return Object.keys(photos).sort((a, b) =>
		name(a) < name(b) ? -1 : name(a) > name(b) ? 1 : a < b ? -1 : 1
	);
};

// The photo before (-1) or after (+1) key in album order, or null at the ends.
export const neighborPhotoKey = (album, key, direction) => {
	const keys = sortedPhotoKeys(album);
	const index = keys.indexOf(key);

	if (index === -1) return null;

	return keys[index + direction] ?? null;
};

// Albums the public site can show: they need at least one photo and a cover.
export const isVisible = (album) =>
	Boolean(album?.coverImage && album.photos?.[album.coverImage]);

// Pinned albums first (in id order), everything else shuffled.
export const orderAlbumIds = (albums, shuffle) => {
	const ids = Object.keys(albums || {})
		.filter((id) => isVisible(albums[id]))
		.sort();

	const pinned = ids.filter((id) => albums[id].pinned);
	const rest = ids.filter((id) => !albums[id].pinned);

	shuffle(rest);

	return [...pinned, ...rest];
};

let sessionOrder;

// Like orderAlbumIds, but the shuffle happens once per page load. Navigating
// back to the home page keeps the same order; a refresh reshuffles. Albums
// added or removed since (e.g. from the admin page) are merged in.
export const sessionAlbumIds = (albums, shuffle) => {
	if (!sessionOrder) {
		sessionOrder = orderAlbumIds(albums, shuffle);

		return sessionOrder;
	}

	const fresh = orderAlbumIds(albums, () => {});
	const pinned = fresh.filter((id) => albums[id].pinned);
	const rest = new Set(fresh.filter((id) => !albums[id].pinned));
	const kept = sessionOrder.filter((id) => rest.has(id));
	const added = [...rest].filter((id) => !kept.includes(id));

	shuffle(added);
	sessionOrder = [...pinned, ...kept, ...added];

	return sessionOrder;
};

export const matchesTags = (album, activeTags) =>
	Object.entries(activeTags).every(
		([tag, active]) => !active || album?.tags?.includes(tag)
	);
